(() => {
  const DEFAULT_ERROR_MESSAGE = '요청 처리 중 오류가 발생했습니다.';

  // theme.liquid의 FILA_API_BASE_URL은 const라 window에 붙지 않음 → 전역 바인딩을 직접 참조 (main: prod, dev: dev API)
  const apiBaseUrl =
    typeof FILA_API_BASE_URL !== 'undefined' ? FILA_API_BASE_URL : 'https://lkk-store-api.fila.co.kr';

  const UnsubscribeApi = {
    config: {
      tokenKey: 'accessToken',
      baseUrl: apiBaseUrl + '/api/v1/users/me',
      // 비로그인 이메일 수신거부 (ERP OOMCRM_CR_RR_015 프록시)
      guestUrl: apiBaseUrl + '/api/v1/users/email/unsubscribe',
      guestParamKey: 'custNoEnc',
    },

    getToken() {
      return window.localStorage.getItem(this.config.tokenKey);
    },

    isLoggedIn() {
      return !!this.getToken();
    },

    redirectToLogin() {
      const returnUrl = window.location.pathname + window.location.search + window.location.hash;
      window.location.href = `/account/login?returnUrl=${encodeURIComponent(returnUrl)}`;
    },

    // 암호화 회원번호(ncpkms:v1:...)에는 '+'가 포함된다.
    // URLSearchParams는 '+'를 공백으로 바꾸므로 decodeURIComponent로 직접 파싱한다.
    getCustNoEnc() {
      const key = this.config.guestParamKey;
      const pair = window.location.search
        .slice(1)
        .split('&')
        .find((part) => part.split('=')[0] === key);
      if (!pair) return null;

      const raw = pair.slice(key.length + 1);
      try {
        return decodeURIComponent(raw) || null;
      } catch (_) {
        return raw || null;
      }
    },

    // FastAPI detail: string(400/502) | [{ msg, loc }](422) | object(connector 에러)
    getErrorDetail(data) {
      const detail = data?.detail ?? data?.message;
      if (Array.isArray(detail)) {
        return detail.map((item) => item?.msg || JSON.stringify(item)).join('\n');
      }
      if (detail && typeof detail === 'object') {
        return detail.error_message || detail.message || JSON.stringify(detail);
      }
      return detail || '';
    },

    createError(res, data) {
      const detail = this.getErrorDetail(data);
      const error = new Error(detail || DEFAULT_ERROR_MESSAGE);
      error.status = res.status;
      error.code = data?.detail?.code || null;
      error.detail = detail;
      return error;
    },

    // 비로그인(영문) 에러 → 영문 문구 종류
    // ERP는 rtnCode(F/E)만 내려주므로 복호화 실패는 rtnMsg 키워드로 구분
    getGuestErrorType(err) {
      if (!err?.status) return 'temporary'; // 네트워크/CORS
      if (err.status === 422) return 'invalid-link';
      if (err.status >= 500 || err.code === 'ERP_UNAVAILABLE') return 'temporary';
      if (/기존과 동일/.test(err.detail || '')) return 'already';
      if (/복호화/.test(err.detail || '')) return 'invalid-link';
      return 'failed';
    },

    async request(url, options) {
      const res = await fetch(url, options);

      let data = null;
      try {
        data = await res.json();
      } catch (_) {
        // ignore non-json response
      }

      if (!res.ok) {
        throw this.createError(res, data);
      }

      // Some FILA APIs return { msg: "SUCCESS" }
      if (data?.msg && data.msg !== 'SUCCESS') {
        throw this.createError(res, data);
      }

      return data;
    },

    // 로그인 회원 (OOMCRM_CR_RR_002)
    unsubscribeEmailMarketing() {
      const payload = {
        marketing: {
          email: false,
        },
      };

      return this.request(this.config.baseUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.getToken()}`,
        },
        body: JSON.stringify(payload),
      });
    },

    // 메일 링크 비인증 (OOMCRM_CR_RR_015) — 암호화 회원번호를 복호화 없이 그대로 전달
    unsubscribeGuestEmail(custNoEnc) {
      const payload = {
        encMbrNo: custNoEnc,
      };

      return this.request(this.config.guestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
    },
  };

  // 성공/에러 공통 커스텀 팝업. onClose는 닫힐 때(버튼·backdrop·ESC) 1회 실행
  const Dialog = {
    open(root, { title, detail, closeLabel, onClose }) {
      const dialog = root.querySelector('[data-unsubscribe-dialog]');
      if (!dialog?.showModal) {
        window.alert([title, detail].filter(Boolean).join('\n\n'));
        onClose?.();
        return;
      }

      dialog.querySelector('[data-unsubscribe-dialog-title]').textContent = title;
      const detailEl = dialog.querySelector('[data-unsubscribe-dialog-detail]');
      detailEl.textContent = detail || '';
      detailEl.hidden = !detail;
      dialog.querySelector('[data-unsubscribe-dialog-close]').textContent = closeLabel;
      dialog.onCloseCallback = onClose || null;
      dialog.showModal();
    },

    bind(root) {
      const dialog = root.querySelector('[data-unsubscribe-dialog]');
      if (!dialog) return;

      dialog.querySelector('[data-unsubscribe-dialog-close]')?.addEventListener('click', () => dialog.close());
      // backdrop 클릭 시 닫기
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog) dialog.close();
      });
      dialog.addEventListener('close', () => {
        const callback = dialog.onCloseCallback;
        dialog.onCloseCallback = null;
        callback?.();
      });
    },
  };

  const init = (scope) => {
    const root =
      scope.querySelector?.('[data-unsubscribe-section]') ||
      (scope.matches?.('[data-unsubscribe-section]') ? scope : null);
    if (!root) return;

    Dialog.bind(root);

    const custNoEnc = UnsubscribeApi.getCustNoEnc();
    const isGuest = !!custNoEnc;

    const confirmBtn = root.querySelector(
      `[data-unsubscribe-content="${isGuest ? 'en' : 'ko'}"] [data-unsubscribe-confirm]`
    );
    if (!confirmBtn) return;

    const closeLabel = confirmBtn.getAttribute('data-close-label') || 'OK';

    // 성공 팝업 → 닫으면 success redirect('/')로 이동
    const completeWith = (msg) => {
      const redirect = confirmBtn.getAttribute('data-success-redirect');
      const goNext = () => {
        if (redirect) window.location.href = redirect;
      };

      if (!msg) {
        goNext();
        return;
      }
      Dialog.open(root, { title: msg, closeLabel, onClose: goNext });
    };

    confirmBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      if (confirmBtn.getAttribute('aria-disabled') === 'true') return;

      if (!isGuest && !UnsubscribeApi.isLoggedIn()) {
        UnsubscribeApi.redirectToLogin();
        return;
      }

      confirmBtn.setAttribute('aria-disabled', 'true');
      confirmBtn.classList.add('loading');

      try {
        if (isGuest) {
          await UnsubscribeApi.unsubscribeGuestEmail(custNoEnc);
        } else {
          await UnsubscribeApi.unsubscribeEmailMarketing();
        }

        completeWith(confirmBtn.getAttribute('data-alert-message'));
      } catch (err) {
        console.error(err);
        const guestErrorType = isGuest ? UnsubscribeApi.getGuestErrorType(err) : null;

        // 이미 수신거부 상태(ERP "기존과 동일")는 목적이 달성된 상태라 성공 흐름으로 처리
        if (guestErrorType === 'already') {
          completeWith(confirmBtn.getAttribute('data-already-message'));
          return;
        }

        // 네트워크/CORS 실패는 status가 없어 err.message(예: "Failed to fetch")를 노출
        // 영문 페이지: ERP 한글 메시지 대신 에러 종류별 영문 문구 노출
        const detail = isGuest
          ? confirmBtn.getAttribute(`data-error-${guestErrorType}`) || ''
          : err?.status
            ? err.detail || ''
            : err?.message || '';
        Dialog.open(root, {
          title: confirmBtn.getAttribute('data-error-message') || DEFAULT_ERROR_MESSAGE,
          detail,
          closeLabel,
        });
      } finally {
        confirmBtn.classList.remove('loading');
        confirmBtn.removeAttribute('aria-disabled');
      }
    });
  };

  document.addEventListener('DOMContentLoaded', () => init(document));
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
